import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART_CHROME } from "@/shared/ui/chart/public";
import { MAGNITUDE_FILL, MARK } from "./chartTheme";
import type { TrendAreaChartProps } from "./contract";

// 채움 그라디언트 id. 한 화면에 추이 차트가 하나뿐이라 고정 id 로 충분하다.
const gradientId = "data-dashboard-trend-fill";

/**
 * 시간에 따른 수집량 추이. 단일 시리즈라 범례를 두지 않는다. 카드 제목이 계열 이름을 알려 준다.
 *
 * 입력 계열은 **빈 버킷을 0 으로 채운 연속 계열**이다. 값이 있는 버킷만 이으면 빈 구간이
 * 직선이 되어 "그동안 꾸준했다" 로 잘못 읽힌다.
 */
export function TrendAreaChart({
  data,
  formatTick,
  formatValue,
  seriesLabel,
}: TrendAreaChartProps) {
  const points = data.map((datum) => ({ atMs: datum.atMs, value: datum.value }));
  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={MAGNITUDE_FILL} stopOpacity={0.35} />
            <stop offset="100%" stopColor={MAGNITUDE_FILL} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={CHART_CHROME.grid} vertical={false} />
        <XAxis
          dataKey="atMs"
          type="number"
          scale="time"
          domain={["dataMin", "dataMax"]}
          tickFormatter={(tick: number) => formatTick(tick)}
          tickLine={false}
          axisLine={false}
          stroke={CHART_CHROME.axis}
          fontSize={MARK.tickFontSize}
          minTickGap={32}
        />
        {/* 눈금도 지표 포맷터를 거친다. 바이트·초 원시값이 차트 눈금에 그대로 표시되지 않게 한다. */}
        <YAxis
          width={56}
          allowDecimals={false}
          tickFormatter={(tick: number) => formatValue(tick)}
          tickLine={false}
          axisLine={false}
          stroke={CHART_CHROME.axis}
          fontSize={MARK.tickFontSize}
        />
        <Tooltip
          labelFormatter={(label: number) => formatTick(label)}
          formatter={(value: number) => [formatValue(value), seriesLabel]}
          contentStyle={{
            background: CHART_CHROME.tooltipBg,
            border: `1px solid ${CHART_CHROME.tooltipBorder}`,
            borderRadius: MARK.radius,
            color: CHART_CHROME.tooltipInk,
            fontSize: MARK.tickFontSize,
          }}
          itemStyle={{ color: CHART_CHROME.tooltipInk }}
        />
        {/* 버킷 카운트라 곡선 보간을 쓰지 않는다. 부드럽게 이으면 버킷 사이에 없던 값이
            생기고, 0 으로 채운 구간이 0 아래로 내려가 보인다. */}
        <Area
          type="linear"
          dataKey="value"
          stroke={MAGNITUDE_FILL}
          strokeWidth={MARK.strokeWidth}
          fill={`url(#${gradientId})`}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
